import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { ArrowRight, TrendingUp, Trophy, Info } from 'lucide-react';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { Slider } from '../../ui/slider';
import { Link } from 'react-router-dom';
import * as ROUTES from '../../../constants/routes';
import { HeroGraphic } from './variants/Variant14';

interface HeroProps {
    authenticated?: boolean;
}

export const Hero: React.FC<HeroProps> = ({ authenticated = false }) => {
    const [seed, setSeed] = useState(8);
    const [wins, setWins] = useState(3);
    const [shares, setShares] = useState(10);
    const [payoutPerShare, setPayoutPerShare] = useState(0);
    const [totalPayout, setTotalPayout] = useState(0);

    useEffect(() => {
        const perShare = seed * wins;
        setPayoutPerShare(perShare);
        setTotalPayout(perShare * shares);
    }, [seed, wins, shares]);

    return (
        <div className="relative min-h-[calc(100vh-4rem)] flex items-center justify-center overflow-y-auto bg-slate-950 py-6 md:py-12">
            {/* Dynamic Background */}
            <div className="absolute inset-0 z-0">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-900/20 via-slate-950 to-slate-950" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-orange-900/10 via-slate-950 to-slate-950" />
                {/* Abstract grid lines */}
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundImage:
                            'linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px)',
                        backgroundSize: '50px 50px',
                    }}
                ></div>
            </div>

            <div className="container relative z-10 px-4 mx-auto py-6 md:py-0">
                <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                    {/* Left Column: Branding & Intro */}
                    <div className="text-center lg:text-left space-y-8">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                        >
                            <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold tracking-tight text-white leading-tight">
                                <span className="block">Trade the</span>
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-blue-300">
                                    Tournament.
                                </span>
                            </h1>
                            <p className="mt-4 md:mt-6 text-base md:text-lg text-slate-400 max-w-lg mx-auto lg:mx-0 leading-relaxed">
                                The first fantasy market where you buy shares in
                                teams. Points are determined by our unique{' '}
                                <span className="text-orange-400 font-semibold">
                                    Seed × Wins × Shares
                                </span>{' '}
                                protocol.
                            </p>
                        </motion.div>

                        {/* Trust/Social Proof */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.8 }}
                            className="pt-8 flex flex-wrap items-center justify-center lg:justify-start gap-6 text-slate-500 text-sm"
                        >
                            <div className="flex items-center gap-2">
                                <TrendingUp className="w-4 h-4 text-green-500" />
                                <span>$1000 Budget</span>
                            </div>
                            <div className="w-1 h-1 rounded-full bg-slate-800" />
                            <div className="flex items-center gap-2">
                                <Trophy className="w-4 h-4 text-orange-500" />
                                <span>Compete for Points</span>
                            </div>
                        </motion.div>
                    </div>

                    {/* Right Column: Animated Hero Graphic */}
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                        className="relative"
                    >
                        <HeroGraphic />
                    </motion.div>
                </div>
            </div>
        </div>
    );
};
